import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { StorePackageSuccess } from "../../StorePackageModal/components/StorePackageSuccess";
import { StoreProductOrder } from "../../StoreProductOrder";
import { StoreProductOrderFooter } from "../../StoreProductOrderFooter";
import { StoreStackNavigatorParamList } from "../../StoreStackNavigator";
import { StoreProductOrderPageQuery } from "./__generated__/StoreProductOrderPageQuery.graphql";

export const StoreProductOrderPage = () => {
  const route =
    useRoute<RouteProp<StoreStackNavigatorParamList, "ProductOrder">>();

  const [queryRef, loadQuery] = useQueryLoader<StoreProductOrderPageQuery>(
    StoreProductOrderPageQueryString
  );

  const orderId = route.params.orderId;
  const memberPackageId = route.params.productId;

  useEffect(() => {
    if (orderId) {
      loadQuery(
        { orderId, memberPackageId },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, memberPackageId, orderId]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? <StoreProductOrderPageInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<StoreProductOrderPageQuery>;
};
const StoreProductOrderPageInner = ({ queryRef }: InnerProps) => {
  const insets = useSafeAreaInsets();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const navigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();

  const data = usePreloadedQuery(StoreProductOrderPageQueryString, queryRef);

  const packageDisplayName = usePackageDisplayName(data.memberPackage?.package);

  const onBackPress = () => {
    navigation.goBack();
  };

  return (
    <Box
      bg="surface-primary"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
    >
      <Container flex={1} paddingHorizontal={0}>
        <ScreenHeader
          title={packageDisplayName}
          leftItem={
            <Button
              variant="secondary"
              accessibilityLabel={t`Back Button`}
              leftIcon={<ArrowLeftIcon />}
              onPress={onBackPress}
              testID="mobile-header-back-button"
            />
          }
        />
        <ScrollView style={{ paddingTop: 16 }}>
          <StoreProductOrder
            memberPackageRef={data.memberPackage}
            orderRef={data.order}
          />
        </ScrollView>
        <StoreProductOrderFooter
          orderRef={data.order}
          memberPackageRef={data.memberPackage}
          onSuccessfulPurchase={() => {
            setIsSuccessModalOpen(true);
          }}
        />
        {isSuccessModalOpen ? (
          <StandardModal
            isOpen={isSuccessModalOpen}
            content={
              <StorePackageSuccess packageRef={data.memberPackage?.package} />
            }
            hideCloseButton
            disableContentScroll
          />
        ) : null}
      </Container>
    </Box>
  );
};

const StoreProductOrderPageQueryString = graphql`
  query StoreProductOrderPageQuery($orderId: ID!, $memberPackageId: ID!) {
    memberPackage: node(id: $memberPackageId) {
      ... on MemberPackage {
        ...StoreProductOrder_memberPackage
        ...StoreProductOrderFooter_memberPackage
        package {
          ...usePackageDisplayName_package
          ...StorePackageSuccess_package
        }
      }
    }
    order: node(id: $orderId) {
      ... on Order {
        ...StoreProductOrder_order
        ...StoreProductOrderFooter_order
      }
    }
  }
`;
